/**
 * App surface manifests — what screens the inspector has, and what a user
 * does on each.
 *
 * This is the SOURCE OF TRUTH for:
 *   - `KNOWN_APP_TAB_SEGMENTS` (client/src/lib/app-navigation.ts), which
 *     decides which tab segments the inspector `navigate` command accepts;
 *   - the route↔surface coverage tests, which fail CI when a new screen
 *     ships without a manifest.
 *
 * ## Adding a screen
 *
 * Add a manifest here and a `kind: "screen"` entry in the route table
 * (client/src/lib/app-routes.ts) pointing at its `id`. The coverage tests
 * fail both ways — a screen route with no manifest, and a manifest no route
 * renders.
 */

/** Stable surface id. */
export type AppSurfaceId = (typeof APP_SURFACES)[number]["id"];

export interface AppSurfaceManifest {
  id: string;
  /**
   * The path `ui_navigate` sends the user to. Must be one of this surface's
   * own `routePatterns`.
   */
  canonicalPath: string;
  /**
   * Every route pattern that renders this surface, including deep links and
   * aliases. One surface legitimately owns many (all `evals/**` detail
   * routes are the Evaluate screen), which is why coverage is by id rather
   * than by a one-route/one-manifest string match.
   */
  routePatterns: readonly string[];
  /**
   * Tab segments that resolve to this surface, POST-normalization (see
   * `normalizeHostedHashTab`). These are the `ui_navigate` targets and the
   * values `pathnameToActiveTab` returns — not necessarily the first path
   * segment (legacy `/hosts` resolves to the `clients` tab, `/servers` to
   * `connectors`).
   */
  navSegments: readonly string[];
  /** Human label, matching the sidebar where one exists. */
  title: string;
  /** One line: what this screen is for. Model-facing. */
  purpose: string;
  /** What users actually do here. Model-facing; keep to real actions. */
  userActivities: readonly string[];
  /**
   * Not reachable in hosted deployments — this field is the SOURCE OF TRUTH
   * for that, and `hosted-tab-policy.ts` derives its block list from it. Set
   * it only when the screen genuinely cannot work hosted (Tracing needs the
   * local OTLP collector); a screen that merely isn't ready yet belongs
   * behind a feature flag instead.
   * Kept out of the atlas when the atlas is built for a hosted surface, so
   * the model isn't handed a map to a door that is locked.
   */
  hostedBlocked?: boolean;
}

export const APP_SURFACES = [
  {
    // sphinx: Clients and Connectors are ONE surface — "Client Connectors".
    // sphinx does not emulate clients, it drives the real ones, so "which
    // client" is not a separate page: it is the header of this one, and the
    // connector list below is scoped to it.
    //
    // `/servers` is the legacy route (redirects here); `servers` the legacy
    // tab segment (aliased to `connectors` in hosted-tab-policy.ts). `/clients`
    // redirects here too, while `/clients/:hostId` stays as this surface's
    // per-client config canvas (offered for the two direct-API clients).
    id: "connectors",
    canonicalPath: "/connectors",
    routePatterns: ["clients/:hostId", "connectors"],
    // `client-config` renders nothing of its own (it redirects here), but it
    // IS still a tab segment that resolves to this surface, so it stays a
    // valid `ui_navigate` target and a valid `pathnameToActiveTab` result.
    // `clients` is here for the same reason.
    navSegments: ["connectors", "client-config", "clients"],
    title: "Client Connectors",
    purpose:
      "Pick a client (Claude Desktop, Codex CLI, the direct APIs, …) and set up what it runs with: whether it is enabled, which models it runs, and which MCP connectors are attached to it. The starting point for most work — most other screens act on a connector connected here.",
    userActivities: [
      "Move between clients and see which one everything below is scoped to",
      "Enable or disable a client for eval runs",
      "Choose the models a client runs (several allowed, one run per model)",
      "Attach or detach a connector for the selected client",
      "Add an MCP server by URL (HTTP) or command (STDIO)",
      "Connect, disconnect, reconnect, or remove a connector",
      "Authorize a connector that requires OAuth",
      "Edit a connector's transport, headers, environment variables, or auth method",
    ],
  },
  {
    id: "host-compare",
    canonicalPath: "/host-compare",
    routePatterns: ["host-compare", "capabilities/:capabilitySlug"],
    navSegments: ["host-compare"],
    title: "Compare",
    purpose:
      "Compare MCP feature support across hosts — which clients implement which parts of the protocol.",
    userActivities: [
      "Compare capability support across several hosts",
      "Open a single capability's support page",
    ],
  },
  {
    id: "playground",
    canonicalPath: "/playground",
    routePatterns: ["playground"],
    // `chat` renders nothing of its own (it redirects here), but it IS still
    // a tab segment that resolves to this surface — so it stays a valid
    // `ui_navigate` target. Same split as `client-config` → Connect.
    navSegments: ["playground", "chat"],
    title: "Playground",
    purpose:
      "Test a connected MCP server against a real model, and preview MCP Apps / ChatGPT apps UI. This is where tool calls are actually run and their results rendered.",
    userActivities: [
      "Chat with a model that has the selected servers' tools",
      "Select a tool and fill in its parameters",
      "Execute a tool and inspect the result",
      "Emulate app context: theme, device, display mode, locale, time zone",
    ],
  },
  {
    id: "evals",
    canonicalPath: "/evals",
    routePatterns: [
      "evals",
      "evals/create",
      "evals/suite/:suiteId",
      "evals/suite/:suiteId/edit",
      "evals/suite/:suiteId/runs/:runId",
      "evals/suite/:suiteId/test/:testId",
      "evals/suite/:suiteId/test/:testId/edit",
    ],
    navSegments: ["evals"],
    title: "Evaluate",
    purpose:
      "Build and run eval suites against a host: test cases with expected tool calls, scored over repeated runs.",
    userActivities: [
      "Create or edit an eval suite and its test cases",
      "Run a suite and watch its runs",
      "Generate suggested test cases for a suite",
      "Open a run to inspect each step, tool call, and score",
      "Compare runs",
      "Review eval results for a commit under Runs",
      "Open a CI run's details under Runs",
    ],
    // Authoring tools register from Suites mode only. Runs mode is read-only
    // review of results CI already produced (runs start from CI, not this
    // screen), so it contributes its snapshot but no tools.
  },
  {
    id: "queue",
    canonicalPath: "/queue",
    routePatterns: ["queue"],
    navSegments: ["queue"],
    title: "Queue",
    purpose:
      "Watch the sphinx job queue: connected Mac runners with their capabilities and slots, and every job with its state, priority and assigned runner.",
    userActivities: [
      "See which runners are connected and what they can run",
      "Raise or lower a queued job's priority",
      "Cancel a queued or running job",
      "Jump from a job to its run",
    ],
  },
  {
    id: "tools",
    canonicalPath: "/tools",
    routePatterns: ["tools"],
    navSegments: ["tools"],
    title: "Tools",
    purpose:
      "List and invoke the tools a connected MCP server exposes, without a model in the loop.",
    userActivities: ["Browse a server's tools", "Invoke a tool directly"],
  },
  {
    id: "resources",
    canonicalPath: "/resources",
    routePatterns: ["resources"],
    navSegments: ["resources"],
    title: "Resources",
    purpose: "List and read the resources a connected MCP server exposes.",
    userActivities: [
      "Browse a server's resources and resource templates",
      "Read a resource, or resolve and read a template",
    ],
  },
  {
    id: "prompts",
    canonicalPath: "/prompts",
    routePatterns: ["prompts"],
    navSegments: ["prompts"],
    title: "Prompts",
    purpose: "List and render the prompts a connected MCP server exposes.",
    userActivities: [
      "Browse a server's prompts",
      "Render a prompt with arguments",
    ],
  },
  {
    id: "tasks",
    canonicalPath: "/tasks",
    routePatterns: ["tasks"],
    navSegments: ["tasks"],
    title: "Tasks",
    purpose:
      "Inspect long-running MCP tasks a connected server exposes, and their status.",
    userActivities: ["Browse a server's tasks", "Inspect a task's status"],
  },
  {
    id: "skills",
    canonicalPath: "/skills",
    routePatterns: ["skills"],
    navSegments: ["skills"],
    title: "Skills",
    purpose: "View, add, and manage the skills available to hosts.",
    userActivities: ["Browse skills", "Add or edit a skill"],
  },
  {
    id: "oauth-flow",
    canonicalPath: "/oauth-flow",
    routePatterns: ["oauth-flow"],
    navSegments: ["oauth-flow"],
    title: "OAuth Debugger",
    purpose:
      "Step through an MCP server's OAuth flow and see exactly what happens at each stage — discovery, registration, authorization, token exchange.",
    userActivities: [
      "Run an OAuth flow against a server step by step",
      "Inspect discovery metadata and each request/response",
    ],
    // The agent can prefill the config form, advance ONE step at a time
    // (approval-gated), and reset. Consent stays structurally human: the
    // authorization step opens a sign-in popup on the third party's page,
    // which the agent cannot complete. See groups/oauth-flow.ts.
  },
  {
    id: "tracing",
    canonicalPath: "/tracing",
    routePatterns: ["tracing"],
    navSegments: ["tracing"],
    title: "Tracing",
    purpose: "Inspect traces of chat turns and tool calls.",
    userActivities: ["Review turn traces"],
    hostedBlocked: true,
  },
  {
    id: "settings",
    canonicalPath: "/settings",
    routePatterns: ["settings", "settings/api-keys"],
    navSegments: ["settings"],
    title: "Settings",
    purpose: "Application settings, including API keys.",
    userActivities: ["Change app settings", "Manage API keys"],
  },
] as const satisfies readonly AppSurfaceManifest[];

/**
 * The manifests as the wide interface.
 *
 * `APP_SURFACES` is `as const satisfies` so `AppSurfaceId` can be a literal
 * union — which narrows every entry to its own literal type, where optional
 * fields an entry didn't set don't exist. Read through this instead.
 */
export function listAppSurfaces(): readonly AppSurfaceManifest[] {
  return APP_SURFACES as readonly AppSurfaceManifest[];
}

const surfacesById = new Map<string, AppSurfaceManifest>(
  listAppSurfaces().map((s) => [s.id, s])
);

export function getAppSurface(id: string): AppSurfaceManifest | undefined {
  return surfacesById.get(id);
}

export function isAppSurfaceId(value: unknown): value is AppSurfaceId {
  return typeof value === "string" && surfacesById.has(value);
}

const surfacesByNavSegment = new Map<string, AppSurfaceManifest>(
  listAppSurfaces().flatMap((s) => s.navSegments.map((seg) => [seg, s]))
);

/**
 * Resolve a normalized tab segment (what `resolveUiNavigationTarget` returns
 * and `pathnameToActiveTab` produces) to its surface manifest. Unambiguous:
 * the coverage test asserts no segment is claimed by two surfaces.
 */
export function getAppSurfaceByNavSegment(
  segment: string
): AppSurfaceManifest | undefined {
  return surfacesByNavSegment.get(segment);
}

/**
 * Every tab segment that resolves to a known surface — the derived
 * replacement for the hand-maintained `KNOWN_APP_TAB_SEGMENTS`.
 */
export function listAppSurfaceNavSegments(): string[] {
  const out = new Set<string>();
  for (const surface of APP_SURFACES) {
    for (const segment of surface.navSegments) out.add(segment);
  }
  return [...out];
}

/**
 * Nav segments a hosted deployment cannot serve — the manifests are the
 * source of truth, and `hosted-tab-policy.ts` is the only caller.
 *
 * Reads through `listAppSurfaces()` rather than `APP_SURFACES` directly:
 * the const assertion narrows each entry to its own literal type, so an
 * optional field is absent from the ones that never set it.
 */
export function listHostedBlockedNavSegments(): string[] {
  const out = new Set<string>();
  for (const surface of listAppSurfaces()) {
    if (!surface.hostedBlocked) continue;
    for (const segment of surface.navSegments) out.add(segment);
  }
  return [...out];
}
